import React from 'react';
import { CalendarDaysIcon, CheckIcon, FileTextIcon, LandmarkIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { ConsultationCta } from '../components/ConsultationCta';
const LIBRARY_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/9cf04be8-e35c-4213-877b-22b8f1cc61d1.jpg';
const FAMILY_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/d98bb839-d37e-415e-8f36-2ae938f2a0f2.jpg';
const commitments = ['Documents read carefully and followed exactly as written', 'Records kept so any account can be reconstructed on request', 'Communication in plain language, in writing where it matters', 'Conflicts of interest disclosed and avoided', 'Assets held separately and never commingled', 'Response to family inquiries within one business day'];
const professionalRole = [{
  icon: LandmarkIcon,
  title: 'Banking & asset management',
  body: 'Administering estate assets and coordinating directly with banking institutions.'
}, {
  icon: FileTextIcon,
  title: 'Financial & insurance oversight',
  body: 'Managing distributions, paying expenses, and navigating health insurance claims, benefits and medical billing.'
}, {
  icon: CalendarDaysIcon,
  title: 'Care coordination',
  body: 'Organizing daily logistics and communicating transparently with families, beneficiaries and professional advisors.'
}];
const team = [{
  name: 'Margaret Halstead',
  role: 'Founder, Licensed Professional Fiduciary',
  bio: 'Twenty years administering trusts, conservatorships and estates, with particular focus on matters involving family disagreement or complex care needs.'
}, {
  name: 'David Okonkwo',
  role: 'Senior Fiduciary Administrator',
  bio: 'Oversees trust and estate accounting, tax coordination and the reporting schedules that keep every matter current with courts and beneficiaries.'
}, {
  name: 'Ana Ruiz',
  role: 'Care Manager',
  bio: 'Assesses needs, arranges in-home support and monitors care plans so adjustments happen before a situation becomes urgent.'
}];
export function About() {
  return <main data-id="element-136" data-mp="1stvk91tcdoiq">
      <PageHero breadcrumb="About Us" eyebrow="ABOUT US" title="A Professional Partner for Important Responsibilities" description="We provide fiduciary and administrative support to individuals and families who require dependable assistance with trusts, estates, personal affairs and ongoing care-related responsibilities." data-id="element-137" data-mp="1xvb5ouzflx1n" />

      <section className="bg-white" data-id="element-138" data-mp="2cie2t0jue3og">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-24 lg:grid-cols-2 lg:items-start lg:gap-20" data-id="element-139" data-mp="2glstcl5eq2g5">
          <div data-id="element-140" data-mp="2llf9b2cmdwsn">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-141" data-mp="2ll5en1gmr1gm">OUR APPROACH</p>
            <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-navy-900 sm:text-[42px]" data-id="element-142" data-mp="m8ky4sdirotz">
              Careful administration, handled quietly and consistently
            </h2>
            <p className="mt-7 text-[17px] leading-relaxed text-ink/75" data-id="element-143" data-mp="jnvbv2vcf7yv">
              Responsibilities such as administering a trust, settling an estate or managing
              another person's day-to-day affairs require patience, organization and discretion. We
              take on that work so families can focus on the people involved rather than the
              paperwork.
            </p>
            <p className="mt-5 text-[17px] leading-relaxed text-ink/75" data-id="element-144" data-mp="12mr1s206506w">
              Our services are practical and clearly defined: administering assets, making
              distributions, keeping records, coordinating care and reporting to the people
              entitled to know. Nothing is improvised, and nothing is left undocumented.
            </p>
            <p className="mt-5 text-[17px] leading-relaxed text-ink/75" data-id="element-145" data-mp="ooiyzdggb4nt">
              We keep a deliberately small caseload. It means each client receives direct contact
              with the people doing the work, and it means we can respond when something needs
              attention the same week rather than the next quarter.
            </p>
          </div>
          <img src={LIBRARY_IMAGE} alt="A wood-paneled private office with built-in bookshelves and a conference table" className="h-[480px] w-full object-cover" data-id="element-146" data-mp="15vhaa17t292l" />
        </div>
      </section>

      <section className="bg-mist" data-id="element-147" data-mp="w2jljdc7znvj">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-24 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-20" data-id="element-148" data-mp="1ju75tcez80ou">
          <img src={FAMILY_IMAGE} alt="A family member sitting with an older relative in a living room" className="h-[440px] w-full object-cover" data-id="element-149" data-mp="1os8q20o5261w" />
          <div data-id="element-150" data-mp="1eucbszzwmuzk">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-151" data-mp="1etswlr1n2kp8">OUR COMMITMENTS</p>
            <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-navy-900 sm:text-[42px]" data-id="element-152" data-mp="3viganiss1mdu">
              What you can expect from us
            </h2>
            <ul className="mt-9 space-y-4" data-id="element-153" data-mp="3s1kd8vpm2hu0">
              {commitments.map(item => <li key={item} className="flex gap-4" data-id="element-154" data-mp="3n3930jvem5b9">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-155" data-mp="3n2r239xsr2x0" />
                  <span className="text-[16px] leading-relaxed text-ink/80" data-id="element-156" data-mp="5u420lgzk5wa">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white" data-id="element-157" data-mp="39cdu57kwno7o">
        <div className="mx-auto max-w-shell px-6 py-24" data-id="element-158" data-mp="3999g7ihn1e3t">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20" data-id="element-159" data-mp="34du4aw36xsa1">
            <div data-id="element-160" data-mp="3j1nkahw6uwt5">
              <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-161" data-mp="3j273e1xkrr8r">
                OUR PROFESSIONAL ROLE
              </p>
              <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-navy-900 sm:text-[42px]" data-id="element-162" data-mp="3e8uf5swt5mgj">
                Fiduciary and administrative, from banking to care logistics
              </h2>
              <p className="mt-7 text-[17px] leading-relaxed text-ink/75" data-id="element-163" data-mp="2lk70whah7xch">
                Our work is fiduciary and administrative. We provide the operational structure and
                oversight required to manage both financial and healthcare logistics seamlessly.
              </p>
            </div>
            <div data-id="element-164" data-mp="3p1k9lawcgha2">
              <ul className="divide-y divide-slate-200 border-y border-slate-200" data-id="element-165" data-mp="3p23g7vgjnj8s">
                {professionalRole.map(({
                icon: Icon,
                title,
                body
              }) => <li key={title} className="flex gap-5 py-7" data-id="element-166" data-mp="2sddz47cqqunv">
                    <Icon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-167" data-mp="2wdxub5dr7o7d" />
                    <div data-id="element-168" data-mp="31bpv6owdmknp">
                      <h3 className="font-serif text-[21px] text-navy-900" data-id="element-169" data-mp="31aeks68feenm">{title}</h3>
                      <p className="mt-2 text-[16px] leading-relaxed text-ink/75" data-id="element-170" data-mp="36bvglnc1zaxc">{body}</p>
                    </div>
                  </li>)}
              </ul>
              <p className="mt-8 border-l-2 border-gold pl-6 text-[16px] leading-relaxed text-ink/75" data-id="element-171" data-mp="lk22x6sb5gb8">
                While we handle the financial, insurance and administrative sides of care, clinical
                medical opinions remain strictly with qualified healthcare professionals. We work
                right alongside these medical experts, ensuring they have the accurate information
                they need to provide the best care possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-mist" data-id="element-172" data-mp="1ndpi6k7d4n0x">
        <div className="mx-auto max-w-shell px-6 py-24" data-id="element-173" data-mp="1idbbb5hhzj4h">
          <div className="max-w-2xl" data-id="element-174" data-mp="1ibqfzdnczw5k">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-175" data-mp="1de8ajx8jvz2b">OUR TEAM</p>
            <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-navy-900 sm:text-[42px]" data-id="element-176" data-mp="18f572h87y0wg">
              The people handling your matter
            </h2>
          </div>
          <div className="mt-14 grid gap-10 md:grid-cols-3" data-id="element-177" data-mp="37q59scd8i97x">
            {team.map(member => <article key={member.name} className="flex h-full flex-col border-t border-gold pt-7" data-id="element-178" data-mp="32r23kolipwyd">
                <h3 className="font-serif text-[23px] text-navy-900" data-id="element-179" data-mp="2bpvnggohhuny">{member.name}</h3>
                <p className="mt-2 text-[13px] font-medium tracking-[0.08em] text-gold-dark" data-id="element-180" data-mp="2gm3xxgrqkzos">
                  {member.role}
                </p>
                <p className="mt-4 text-[15px] leading-relaxed text-ink/75" data-id="element-181" data-mp="26wu3vvdc16y1">{member.bio}</p>
              </article>)}
          </div>
        </div>
      </section>

      <ConsultationCta heading="Have a responsibility you are unsure how to handle?" description="A short conversation is usually enough to tell you whether professional fiduciary support is the right answer for your situation." data-id="element-182" data-mp="1sznqyhfai2gq" />
    </main>;
}